/* File: src/components/MiniChatBot.js */
import React, { useState, useEffect, useCallback, createContext } from 'react';
import axios from 'axios';
import config from '../config/config';
import ElenaAvatar from './ElenaAvatar';

export const ChatbotContext = createContext();
const DAILY_LIMIT = 30;

export default function MiniChatBot() {
  const [isOpen, setIsOpen]   = useState(false);
  const [messages, setMsgs]   = useState([]);
  const [input, setInput]     = useState('');
  const [loading, setLoading] = useState(false);
  const [remaining, setRem]   = useState(DAILY_LIMIT);
  const [lang, setLang]       = useState((navigator.language || 'de').slice(0,2).toLowerCase());
  const [muted, setMuted]     = useState(false);
  const [voices, setVoices]   = useState([]);

  const todayKey = `chatbot_limit_${new Date().toISOString().slice(0,10)}`;

  useEffect(() => {
    const used = parseInt(localStorage.getItem(todayKey) || '0', 10);
    setRem(Math.max(0, DAILY_LIMIT - used));
  }, [todayKey]);

  const incrementLocalCounter = useCallback(() => {
    const used = parseInt(localStorage.getItem(todayKey) || '0', 10) + 1;
    localStorage.setItem(todayKey, String(used));
    setRem(Math.max(0, DAILY_LIMIT - used));
  }, [todayKey]);

  useEffect(() => {
    if (!('speechSynthesis' in window)) return;
    const loadVoices = () => setVoices(window.speechSynthesis.getVoices() || []);
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  const pickVoice = useCallback(() => {
    if (!voices || voices.length === 0) return null;
    const L = (lang || 'de').toLowerCase();
    return (
      voices.find(v => v.lang?.toLowerCase().startsWith(L) && /female|frau|anna|sara|kat/i.test(v.name)) ||
      voices.find(v => v.lang?.toLowerCase().startsWith(L)) || null
    );
  }, [voices, lang]);

  const speak = useCallback((text) => {
    if (!text || muted || !('speechSynthesis' in window)) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang  = (lang && lang.length === 2) ? `${lang}-${lang.toUpperCase()}` : (lang || 'de-DE');
    u.rate  = 0.95; u.pitch = 1.05;
    const v = pickVoice(); if (v) u.voice = v;
    try { window.speechSynthesis.cancel(); window.speechSynthesis.speak(u); } catch {}
  }, [lang, muted, pickVoice]);

  // letzte Bot-Nachricht (ohne .findLast())
  const lastBotMessage = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m && m.sender === 'bot') return m.text || '';
    }
    return '';
  })();

  const sendMessage = useCallback(async () => {
    if (!input.trim() || loading || remaining <= 0) return;
    const text = input.trim();
    setMsgs(prev => [...prev, { sender: 'user', text }]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(
        `${config.API_BASE_URL}/api/chatbot`,
        { question: text, lang },
        { timeout: 15000 }
      );
      incrementLocalCounter();
      const answer = res?.data?.answer || 'Entschuldigung, ich habe gerade keine Antwort.';
      setMsgs(prev => [...prev, { sender: 'bot', text: answer }]);
      speak(answer);
    } catch {
      setMsgs(prev => [...prev, { sender: 'bot', text: '⚠️ Momentan keine Antwort (Timeout/Überlast). Bitte später erneut versuchen.' }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, remaining, incrementLocalCounter, lang, speak]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <ChatbotContext.Provider value={{ loading, messages }}>
      <div className="relative z-50">
        {/* EIN Einstiegspunkt: Avatar links unten */}
        {!isOpen && (
          <ElenaAvatar
            loading={loading}
            onClick={() => setIsOpen(true)}
            lastMessage={lastBotMessage}
          />
        )}

        {/* Chat-Panel: links unten passend zum Avatar */}
        {isOpen && (
          <div
            className="fixed bottom-24 left-6 w-80 bg-white text-black rounded-lg shadow-xl flex flex-col border border-gray-300"
            role="dialog" aria-label="Elena – KI-Assistentin" aria-modal="false"
          >
            <div className="p-3 bg-blue-600 text-white rounded-t-lg font-semibold flex items-center justify-between">
              <span>Elena – KI-Assistentin</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setMuted(!muted)}
                  title={muted ? 'Sprachausgabe aktivieren' : 'Sprachausgabe deaktivieren'}
                  className="text-white text-sm hover:underline"
                  aria-pressed={muted}
                >
                  {muted ? '🔇' : '🔊'}
                </button>
                <select
                  value={lang}
                  onChange={(e) => setLang(e.target.value)}
                  className="bg-white text-black text-xs rounded p-1"
                  aria-label="Sprache auswählen"
                >
                  <option value="en">EN</option>
                  <option value="de">DE</option>
                  <option value="fr">FR</option>
                  <option value="ar">AR</option>
                  <option value="es">ES</option>
                </select>
                <button
                  onClick={() => setIsOpen(false)}
                  className="ml-2 text-white/90 hover:text-white"
                  aria-label="Chat schließen"
                  title="Chat schließen"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="flex-1 p-3 space-y-2 overflow-y-auto max-h-96" aria-live="polite">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.sender === 'user' ? 'justify-end' : 'items-start space-x-2'}`}>
                  {m.sender === 'bot' && <div className="w-6 h-6 rounded-full bg-emerald-400" aria-hidden="true" />}
                  <div className={`text-sm p-2 rounded-lg max-w-[80%] ${m.sender === 'bot' ? 'bg-gray-200 text-black' : 'bg-blue-100 text-black'}`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {loading && <div className="text-xs text-gray-500">Elena denkt ...</div>}
            </div>

            <div className="p-3 border-t flex">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                className="flex-1 px-2 py-1 border rounded mr-2 text-sm"
                placeholder="Frage stellen..."
                disabled={remaining <= 0}
                aria-label="Ihre Nachricht"
              />
              <button
                onClick={sendMessage}
                className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 disabled:opacity-60"
                disabled={loading || remaining <= 0}
              >
                Senden
              </button>
            </div>

            {remaining <= 0 && (
              <div className="p-2 text-center text-xs text-red-500">
                Tageslimit erreicht – bitte morgen wiederkommen.
              </div>
            )}
          </div>
        )}
      </div>
    </ChatbotContext.Provider>
  );
}
